import { useEffect, useState } from 'react';
import type { Operaciones } from '../api/operaciones';
import type { AnexoVigente, Disponibilidad, Sector } from '../api/ops/negocio';
import { ErrorApi } from '../api/problema';
import { Campo } from '../componentes/Campo';
import { Chip } from '../componentes/Chip';
import { Desplegable } from '../componentes/Desplegable';
import { Icono } from '../componentes/Icono';
import { mensajeDeError } from '../logica/acceso';
import {
    GESTIONA,
    MONEDAS_ALTA,
    PAISES_ALTA,
    TEXTOS_ALTA,
    ZONAS_ALTA,
    borrarBorrador,
    datosDelAlta,
    estadoDireccion,
    guardarBorrador,
    leerBorrador,
    prefijoDeDireccion,
    sePuedeComprobar,
    slugDeNombre,
    slugEscrito,
    validarAlta,
    type BorradorAlta,
    type ErroresAlta,
} from '../logica/altaNegocio';
import { HojaRoja } from './InvitacionHoja';
import { tokenCaptcha } from './InvitacionCaptcha';
import './AltaNegocio.css';

/**
 * «Tu negocio» (1.5, `diseno/plataforma/1.1-1.6-1.10-alta-e-inicio-de-sesion.html`, pantalla `signup2`): nombre, qué va a
 * gestionar (G170), sector de `listBusinessSectors` (G270), dirección web comprobada al escribir (G180), país, moneda y
 * zona (el país rellena las otras dos), código de partner plegado y pie antibot. Lo escrito se guarda en este dispositivo
 * hasta crearlo (G169). Antes de crear el negocio enseña el PDF público del anexo de encargo vigente, el que trae
 * `listBusinessSectors` (`data_processing_terms`, G980), y manda esa versión en `createBusiness` (G201). Se llega desde
 * el alta de un propietario nuevo, tras «Protege tu cuenta» (1.6; tarea 490), y desde «Sin negocio» › «Crear mi negocio»
 * (G132); al crearlo, se entra en él. La dirección de delante del campo y «Libre: tu web será …» salen de `public_url`
 * de checkSlugAvailability (G990).
 */
interface Props {
    ops: Operaciones;
    /** «Volver» (desde «Sin negocio»); en el alta de un propietario nuevo no hay vuelta atrás, como el dibujo. */
    onVolver?: () => void;
    /** «Paso 4 de 4» en el alta de un propietario nuevo (1.1 → 1.2 → 1.6 → 1.5, tarea 490). */
    paso?: number;
    /** Negocio creado: entrar en él (selectBusiness). */
    onCreado: (businessId: string) => Promise<void>;
}

export function AltaNegocio({ ops, onVolver, paso, onCreado }: Props) {
    const [b, setB] = useState<BorradorAlta>(leerBorrador);
    const [slugTocado, setSlugTocado] = useState(() => b.slug !== '' && b.slug !== slugDeNombre(b.nombre));
    const [sectores, setSectores] = useState<Sector[]>([]);
    const [anexo, setAnexo] = useState<AnexoVigente | null>(null);
    const [partner, setPartner] = useState(b.partner !== '');
    const [errores, setErrores] = useState<ErroresAlta>({});
    const [errorForm, setErrorForm] = useState<string | null>(null);
    const [robot, setRobot] = useState(false);
    const [ocupado, setOcupado] = useState(false);
    const [respuesta, setRespuesta] = useState<Disponibilidad | null>(null);
    // El dominio con su barra delante del campo (G990): de la última respuesta con dirección; se queda al seguir escribiendo.
    const [prefijo, setPrefijo] = useState<string | null>(null);

    useEffect(() => {
        let vivo = true;
        ops.listBusinessSectors()
            .then((r) => {
                if (vivo) {
                    setSectores(r.data);
                    // Siempre viene (G991): las altas no se abren sin una versión del anexo en vigor.
                    setAnexo(r.data_processing_terms);
                }
            })
            .catch((e: unknown) => {
                if (vivo) {
                    setErrorForm(mensajeDeError(e, 'negocio'));
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops]);

    // La dirección se comprueba al dejar de escribir (G180); no reserva nada: createBusiness la vuelve a comprobar.
    // Mientras la respuesta no sea de la dirección escrita, se ve «Comprobando…».
    useEffect(() => {
        if (!sePuedeComprobar(b.slug)) {
            return;
        }
        const control = new AbortController();
        const t = setTimeout(() => {
            ops.checkSlugAvailability(b.slug, control.signal)
                .then((r) => {
                    if (!control.signal.aborted) {
                        setRespuesta(r);
                        const delServidor = prefijoDeDireccion(r);
                        if (delServidor !== null) {
                            setPrefijo(delServidor);
                        }
                    }
                })
                .catch(() => undefined);
        }, 450);
        return () => {
            clearTimeout(t);
            control.abort();
        };
    }, [ops, b.slug]);

    const cambiar = (cambio: Partial<BorradorAlta>) => {
        setB((x) => {
            const nuevo = { ...x, ...cambio };
            if (cambio.nombre !== undefined && !slugTocado) {
                nuevo.slug = slugDeNombre(cambio.nombre);
            }
            if (cambio.pais !== undefined) {
                const p = PAISES_ALTA.find(([c]) => c === cambio.pais);
                if (p !== undefined) {
                    nuevo.moneda = p[2];
                    nuevo.zona = p[3];
                }
            }
            guardarBorrador(nuevo);
            return nuevo;
        });
        // Cambiar el nombre también cambia la dirección propuesta.
        const campos: string[] = [...Object.keys(cambio), ...(cambio.nombre !== undefined && !slugTocado ? ['slug'] : [])];
        setErrores((e) => Object.fromEntries(Object.entries(e).filter(([k]) => !campos.includes(k))));
        setErrorForm(null);
    };

    const direccion = estadoDireccion(b.slug, false, respuesta);

    const crear = () => {
        const fallos = validarAlta(b);
        if (fallos.slug === undefined && direccion.error !== undefined) {
            fallos.slug = direccion.error;
        }
        setErrores(fallos);
        if (Object.keys(fallos).length > 0) {
            return;
        }
        if (anexo === null) {
            setErrorForm(TEXTOS_ALTA.sinAnexo);
            return;
        }
        setOcupado(true);
        setErrorForm(null);
        setRobot(false);
        void (async () => {
            try {
                const negocio = await ops.createBusiness(datosDelAlta(b, anexo.version), await tokenCaptcha('alta_negocio'));
                borrarBorrador();
                await onCreado(negocio.id);
            } catch (e) {
                setOcupado(false);
                if (e instanceof ErrorApi && e.codigo === 'captcha_required') {
                    setRobot(true);
                    return;
                }
                const delSlug = e instanceof ErrorApi ? e.errores.find((x) => x.field === 'slug') : undefined;
                if (delSlug !== undefined) {
                    setErrores({ slug: delSlug.code === 'reserved' ? TEXTOS_ALTA.slugReservada : delSlug.code === 'taken' ? TEXTOS_ALTA.slugOcupada : TEXTOS_ALTA.slugFormato });
                    return;
                }
                setErrorForm(mensajeDeError(e, 'negocio'));
            }
        })();
    };

    const errorSlug = errores.slug ?? direccion.error ?? null;
    return (
        <HojaRoja
            volver={onVolver === undefined ? null : { texto: 'Volver', accion: onVolver }}
            paso={paso}
            titulo={TEXTOS_ALTA.titulo}
            sub={TEXTOS_ALTA.sub}
            error={errorForm}
            ocupado={ocupado}
            principal={{ texto: TEXTOS_ALTA.crear, icono: 'store', ocupado: TEXTOS_ALTA.creando, accion: crear }}
            pie={
                <>
                    {robot && (
                        <div role="alert" className="rv-acceso__error">
                            <Icono nombre="shield-alert" />
                            <span>{TEXTOS_ALTA.robot}</span>
                        </div>
                    )}
                    <div className="rv-alta__legal">
                        {/* G201, G242: aceptación de las condiciones del negocio con el anexo de encargo. Texto a validar (asesoría, G262). */}
                        <span className="rv-alta__validar">
                            <Chip estado="pending">Texto a validar</Chip>
                        </span>
                        <span>
                            Al crear el negocio aceptas las <u>condiciones del negocio</u>, con su anexo de encargo del tratamiento: Reservaria trata los datos de tus clientes por cuenta tuya.
                            {anexo !== null && (
                                <>
                                    {' '}
                                    {/* G980 (G930, G962): el PDF público de la versión vigente, sin credencial, antes de que exista el negocio. */}
                                    <a className="rv-alta__anexo" href={anexo.public_url} target="_blank" rel="noopener noreferrer">
                                        Leer el anexo (PDF, versión {anexo.version})
                                    </a>
                                </>
                            )}
                        </span>
                        <span>
                            Este sitio está protegido por reCAPTCHA y se aplican la{' '}
                            <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
                                Política de privacidad
                            </a>{' '}
                            y las{' '}
                            <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">
                                Condiciones del servicio
                            </a>{' '}
                            de Google.
                        </span>
                    </div>
                </>
            }
        >
            <div className="rv-alta__campos">
                <div className="rv-acceso__campo rv-alta__entera">
                    <Campo etiqueta="Nombre del negocio" autocompletar="organization" mayusculas="words" valor={b.nombre} error={errores.nombre} onChange={(v) => { cambiar({ nombre: v }); }} />
                </div>
                <div className="rv-acceso__campo rv-alta__entera">
                    <span className="rv-alta__etiqueta" id="rv-alta-gestiona">Qué vas a gestionar</span>
                    <div role="radiogroup" aria-labelledby="rv-alta-gestiona" className="rv-alta__chips">
                        {GESTIONA.map(([texto]) => {
                            const on = b.gestiona === texto;
                            return (
                                <button key={texto} type="button" role="radio" aria-checked={on} className={`rv-alta__chip${on ? ' rv-alta__chip--on' : ''}`} onClick={() => { cambiar({ gestiona: texto }); }}>
                                    {on && <Icono nombre="check" />}
                                    <span>{texto}</span>
                                </button>
                            );
                        })}
                    </div>
                    {errores.gestiona !== undefined && (
                        <span role="alert" className="rv-alta__error">
                            <Icono nombre="circle-alert" />
                            {errores.gestiona}
                        </span>
                    )}
                </div>
                <div className="rv-acceso__campo rv-alta__entera">
                    <Desplegable etiqueta="Sector" valor={b.sector} opciones={[...(b.sector === '' ? [{ valor: '', texto: 'Elige uno' }] : []), ...sectores.map((s) => ({ valor: s.code, texto: s.name }))]} error={errores.sector} onChange={(v) => { cambiar({ sector: v }); }} />
                </div>
                <div className="rv-acceso__campo rv-alta__entera">
                    <Campo
                        etiqueta="Dirección de tu web"
                        mayusculas="none"
                        prefijo={prefijo ?? undefined}
                        valor={b.slug}
                        error={errorSlug}
                        ayuda={errorSlug === null ? (direccion.ayuda ?? null) : null}
                        onChange={(v) => {
                            setSlugTocado(true);
                            cambiar({ slug: slugEscrito(v) });
                        }}
                    />
                </div>
                <div className="rv-acceso__campo">
                    <Desplegable etiqueta="País" valor={b.pais} opciones={PAISES_ALTA.map(([v, t]) => ({ valor: v, texto: t }))} onChange={(v) => { cambiar({ pais: v }); }} />
                </div>
                <div className="rv-acceso__campo">
                    <Desplegable etiqueta="Moneda" valor={b.moneda} opciones={MONEDAS_ALTA.map(([v]) => ({ valor: v, texto: v }))} onChange={(v) => { cambiar({ moneda: v }); }} />
                </div>
                <div className="rv-acceso__campo rv-alta__entera">
                    <Desplegable etiqueta="Zona horaria" valor={b.zona} opciones={ZONAS_ALTA.map(([v, t]) => ({ valor: v, texto: t }))} onChange={(v) => { cambiar({ zona: v }); }} />
                </div>
                {partner && (
                    <div className="rv-acceso__campo rv-alta__entera">
                        <Campo etiqueta="Código de partner" autocompletar="off" mayusculas="characters" valor={b.partner} ayuda={TEXTOS_ALTA.ayudaPartner} onChange={(v) => { cambiar({ partner: v }); }} />
                    </div>
                )}
            </div>
            <div className="rv-acceso__nota">
                <Icono nombre="info" />
                <span>{TEXTOS_ALTA.nota}</span>
            </div>
            <button
                type="button"
                className="rv-alta__partner"
                aria-expanded={partner}
                onClick={() => {
                    if (partner) {
                        cambiar({ partner: '' });
                    }
                    setPartner(!partner);
                }}
            >
                <Icono nombre={partner ? 'x' : 'ticket'} />
                <span>{partner ? TEXTOS_ALTA.sinPartner : TEXTOS_ALTA.conPartner}</span>
            </button>
        </HojaRoja>
    );
}
